import { ApiError, type components } from '@hive/api';
import type { Lang } from '@hive/i18n';

/**
 * Consultas visuais (HU-126 a 128): o formato da especificação que o núcleo entende
 * (`ReportEngine`), os tipos do catálogo e utilidades da console.
 */

type S = components['schemas'];
export type Catalog = S['hive.core.reports.ReportsController.Catalog'];
export type EntityView = S['hive.core.reports.ReportsController.EntityView'];
export type FieldView = S['hive.core.reports.ReportsController.FieldView'];
export type Result = S['hive.core.reports.ReportEngine.Result'];
export type Saved = S['hive.core.reports.ReportsController.Saved'];
export type LibraryItem = S['hive.core.reports.ReportsController.LibraryItem'];
export type Widget = S['hive.core.reports.DashboardsController.Widget'];
export type Dashboard = S['hive.core.reports.DashboardsController.Dashboard'];
export type WidgetData = S['hive.core.reports.DashboardsController.WidgetData'];
export type Schedule = S['hive.core.reports.ReportSchedules.Schedule'];
export type JsonNode = S['tools.jackson.databind.JsonNode'];

export type Join = 'and' | 'or';
export interface Cond {
  join?: Join;
  field?: string;
  op: string;
  value?: string;
  value2?: string;
  relation?: string;
  filters?: Cond[];
}
export interface CalcArg {
  field?: string;
  value?: string;
  calc?: string;
}
export interface Calc {
  id: string;
  label: string;
  op: string;
  args: CalcArg[];
}
export interface Group {
  field: string;
  bucket?: string;
}
export interface Agg {
  fn: string;
  field?: string;
  label?: string;
}
export interface Spec {
  entity: string;
  columns?: string[];
  filters?: Cond[];
  calcs?: Calc[];
  groupBy?: Group[];
  aggregates?: Agg[];
  sort?: Array<{ key: string; dir: 'asc' | 'desc' }>;
  limit?: number;
  compare?: { days: number };
}

export const VIZ = ['stat', 'kpi', 'gauge', 'bar', 'hbar', 'line', 'area', 'stacked', 'donut', 'table', 'alerts', 'heatmap', 'topology'] as const;
export const FORMATS = ['pdf', 'xlsx', 'csv'] as const;
export const CATEGORIES = ['inventory', 'security', 'alerts', 'changes', 'software', 'contracts', 'people'] as const;
export const BUCKETS = ['', 'hour', 'day', 'week', 'month', 'year'] as const;
/** Operadores que não pedem valor. */
export const NO_VALUE = new Set(['empty', 'not-empty', 'is-true', 'is-false']);

/** O núcleo recebe a especificação como JSON livre. */
export const node = (x: unknown) => x as JsonNode;
export const asSpec = (x: unknown) => (x ?? { entity: 'assets' }) as unknown as Spec;

/** Rótulo no idioma da pessoa (os rótulos do catálogo vêm nos três idiomas). */
export function lbl(m: Record<string, string> | undefined, lang: Lang): string {
  if (!m) return '';
  return m[lang] ?? m.en ?? Object.values(m)[0] ?? '';
}

export interface FieldOpt {
  ref: string;
  label: string;
  type: string;
  ops: string[];
  values: boolean;
  relation?: string;
}

/** Todos os campos que a entidade oferece: os dela, os das listas relacionadas ("lista.campo") e os personalizados. */
export function fieldsOf(e: EntityView | undefined, lang: Lang, calcs: Calc[] = []): FieldOpt[] {
  if (!e) return [];
  const out: FieldOpt[] = (e.fields ?? []).map((f) => ({
    ref: f.id ?? '',
    label: lbl(f.label, lang),
    type: f.type ?? 'text',
    ops: f.ops ?? [],
    values: !!f.values,
  }));
  for (const c of (e.customFields ?? []) as Array<Record<string, unknown>>) {
    out.push({ ref: String(c.id), label: lbl(c.label as Record<string, string>, lang) || String(c.id), type: 'text', ops: (c.ops as string[]) ?? [], values: false });
  }
  for (const r of e.relations ?? []) {
    for (const f of r.fields ?? []) {
      out.push({
        ref: `${r.id}.${f.id}`,
        label: `${lbl(r.label, lang)} · ${lbl(f.label, lang)}`,
        type: f.type ?? 'text',
        ops: f.ops ?? [],
        values: !!f.values,
        relation: r.joined ? undefined : r.id,
      });
    }
  }
  for (const c of calcs) {
    const type = c.op === 'concat' ? 'text' : 'number';
    out.push({ ref: c.id, label: c.label || c.id, type, ops: type === 'text' ? ['is', 'is-not', 'contains', 'not-contains', 'starts-with', 'empty', 'not-empty'] : ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'empty', 'not-empty'], values: false });
  }
  return out;
}

/** A consulta tem o que mostrar? (campos escolhidos, ou agrupamento/total). */
export function runnable(s: Spec): boolean {
  return (s.columns?.length ?? 0) > 0 || (s.groupBy?.length ?? 0) > 0 || (s.aggregates?.length ?? 0) > 0;
}

/** Tira da especificação blocos incompletos (campo ou valor por escolher) antes de mandar ao núcleo. */
export function clean(s: Spec): Spec {
  const okCond = (c: Cond): boolean =>
    c.op === 'has' || c.op === 'has-not' ? !!c.relation : !!c.field && !!c.op && (NO_VALUE.has(c.op) || !!c.value?.trim()) && (c.op !== 'between' || !!c.value2?.trim());
  const filters = (s.filters ?? []).filter(okCond).map((c) => (c.filters ? { ...c, filters: c.filters.filter(okCond) } : c));
  const calcs = (s.calcs ?? []).filter((c) => c.args.every((a) => a.field || a.value || a.calc));
  const aggregates = (s.aggregates ?? []).filter((a) => a.fn === 'count' || !!a.field);
  const groupBy = (s.groupBy ?? []).filter((g) => !!g.field);
  const out: Spec = { ...s, filters, calcs, aggregates, groupBy };
  if (!groupBy.length && !aggregates.length) delete out.compare;
  return out;
}

/** Baixa um arquivo gerado pelo núcleo (exportação). */
export async function downloadFrom(res: { data?: unknown; response: Response }, fallback: string) {
  if (!res.response.ok || !res.data) throw new ApiError(res.response.status, { status: res.response.status, title: res.response.statusText });
  const cd = res.response.headers.get('Content-Disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? fallback;
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Valor de uma célula para mostrar (datas e números no formato do idioma). */
export function cell(v: unknown, type: string | undefined, f: { date: (x: string) => string; dateTime: (x: string) => string; number: (x: number) => string }, yes: string, no: string): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? yes : no;
  if (type === 'number' && typeof v === 'number') return f.number(v);
  if (type === 'date' && typeof v === 'string') return v.length <= 10 ? f.date(v) : f.dateTime(v);
  return String(v);
}

/** Série para gráficos: rótulo (colunas de agrupamento) e valor (primeira coluna numérica depois delas). */
export function series(r: Result | undefined, groups?: number): Array<{ label: string; value: number }> {
  if (!r?.columns?.length || !r.rows) return [];
  const cols = r.columns;
  let vi = cols.findIndex((c, i) => c.type === 'number' && i >= (groups ?? 1));
  if (vi < 0) vi = cols.findIndex((c) => c.type === 'number');
  if (vi < 0) return [];
  const li = Array.from({ length: vi }, (_, i) => i).filter((i) => i !== vi);
  return r.rows.map((row) => ({
    label: (li.length ? li : [0]).map((i) => (row[i] == null || row[i] === '' ? '—' : String(row[i]))).join(' · '),
    value: Number(row[vi] ?? 0),
  }));
}

export const SEV_TONE: Record<string, 'crit' | 'warn' | 'info' | 'neutral'> = { critical: 'crit', major: 'warn', high: 'crit', warning: 'warn', medium: 'warn', info: 'info', low: 'info' };

/** Posição de um widget na grade de 12 colunas do painel. */
export interface CellT {
  widgetId: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Sem sobreposição: quem foi movido fica onde está; os demais descem até caber (como uma grade de painel). */
export function resolve(cells: CellT[], moved?: string): CellT[] {
  const hit = (a: CellT, b: CellT) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
  const first = cells.filter((c) => c.widgetId === moved);
  const rest = cells.filter((c) => c.widgetId !== moved).sort((a, b) => a.y - b.y || a.x - b.x);
  const placed: CellT[] = [...first];
  for (const c of rest) {
    const n = { ...c };
    while (placed.some((p) => hit(p, n))) n.y += 1;
    placed.push(n);
  }
  return placed;
}

